import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { outreach, outreachQueries } from '@round/api';
import { AlertDialog, Button, lucideIcons, toast } from '@round/creatorbase-ui';
import { useCancelOutreachRequestMutation } from '@round/creatorbase-helpers/api-hooks';

const keys = outreachQueries.outreachRequests.keys;
const utils = outreachQueries.outreachRequests.utils.cancel;

/** A finished or already stopped request has nothing left running to stop. */
const UNCANCELLABLE_STATUSES: outreach.OutreachStatus[] = ['complete', 'cancelled'];

const CANCEL_LABEL = 'Cancel request';

type Props = {
    request: outreach.OutreachRequest;
    className?: string;
};

export const CancelOutreachRequestButton = ({ request, className }: Props) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const queryClient = useQueryClient();

    const { cancel, isLoading } = useCancelOutreachRequestMutation(request.id, queryClient, {
        onError: (error) => {
            // Someone else finishing or stopping the request first answers 400 naming the status it
            // is really in. Saying so and refetching leaves the page honest about where it stands,
            // rather than showing a request that can still be stopped when it can't.
            toast.error(utils.getErrorMessage(error));
        },
        onSettled: () => {
            setIsConfirmOpen(false);
            queryClient.invalidateQueries({ queryKey: keys.detail(request.id) });
            queryClient.invalidateQueries({ queryKey: keys.lists() });
            queryClient.invalidateQueries({ queryKey: keys.infiniteLists() });
        },
    });

    const handleOpenChange = (isOpen: boolean) => {
        // Dismissing mid-flight would leave the request being stopped with nothing saying so.
        if (isLoading) {
            return;
        }

        setIsConfirmOpen(isOpen);
    };

    const handleConfirm = () => {
        cancel();
    };

    if (UNCANCELLABLE_STATUSES.includes(request.status)) {
        return null;
    }

    return (
        <>
            <Button variant="outline" size="sm" className={className} onClick={() => setIsConfirmOpen(true)}>
                <lucideIcons.Ban aria-hidden="true" />
                {CANCEL_LABEL}
            </Button>

            <AlertDialog open={isConfirmOpen} onOpenChange={handleOpenChange}>
                <AlertDialog.Content size="sm">
                    <AlertDialog.Header>
                        <AlertDialog.Title>Cancel this request?</AlertDialog.Title>
                        <AlertDialog.Description>
                            Collecting stops for “{request.name}” and no further creators will be found for it. Anything
                            found so far stays on the request. This can't be undone.
                        </AlertDialog.Description>
                    </AlertDialog.Header>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel variant="outline" size="sm" disabled={isLoading}>
                            Keep request
                        </AlertDialog.Cancel>
                        <AlertDialog.Action
                            variant="destructive"
                            size="sm"
                            onClick={handleConfirm}
                            disabled={isLoading}
                            isLoading={isLoading}
                        >
                            {CANCEL_LABEL}
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog>
        </>
    );
};
